(() => {
  const storageKey = 'nini:favorites:v1';
  const externalGlobalKeys = [
    'niniWishlistItems',
    'niniFavoritesItems',
    '__niniWishlistItems',
    '__niniFavoritesItems',
    'swymWishlistItems',
    'wishlistItems',
    'favoriteItems',
  ];
  let lastExternalSignature = '';

  const track = (eventName, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      nini_source: 'favorite_button',
      ...detail,
    });
  };

  const normalizeExternalFavorite = (item, index) => {
    if (!item || typeof item !== 'object') return null;
    return {
      id: item.id || item.productId || item.product_id || item.variantId || item.variant_id || item.url || index,
      title: item.title || item.productTitle || item.product_title || item.name || '',
      url: item.url || item.productUrl || item.product_url || item.du || '',
      price: item.price || item.priceText || item.formatted_price || item.product_price || '',
      image: item.image || item.imageUrl || item.image_url || item.featured_image || item.featuredImage || item.product_image || '',
      category: item.category || item.productType || item.product_type || item.type || '',
      productType: item.productType || item.product_type || item.type || '',
      variantId: item.variantId || item.variant_id || '',
      compareAtPrice: item.compareAtPrice || item.compare_at_price || item.comparePrice || '',
      badge: item.badge || item.tag || '',
      available: item.available !== false,
      addedAt: item.addedAt || item.createdAt || item.created_at || new Date().toISOString(),
      source: item.source || 'wishlist_app',
    };
  };

  const uniqueById = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const id = String(item?.id || item?.url || '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const readExternalFavorites = () => {
    const externalItems = [];
    externalGlobalKeys.forEach((key) => {
      const value = window[key];
      if (!Array.isArray(value)) return;
      value.forEach((item, index) => {
        const normalized = normalizeExternalFavorite(item, index);
        if (normalized) externalItems.push(normalized);
      });
    });

    document.querySelectorAll('[data-nini-external-wishlist]').forEach((script) => {
      try {
        const parsed = JSON.parse(script.textContent || '[]');
        if (Array.isArray(parsed)) {
          parsed.forEach((item, index) => {
            const normalized = normalizeExternalFavorite(item, index);
            if (normalized) externalItems.push(normalized);
          });
        }
      } catch (_error) {
        // Keep local favorites working if an app injects malformed JSON.
      }
    });

    return uniqueById(externalItems);
  };

  const syncExternalIfChanged = () => {
    const externalItems = readExternalFavorites();
    const signature = JSON.stringify(externalItems.map((item) => `${item.id}:${item.addedAt || ''}`));
    if (signature === lastExternalSignature) return;
    lastExternalSignature = signature;
    if (externalItems.length > 0) {
      syncButtons();
      track('nini_wishlist_external_detected', { favorite_count: externalItems.length });
    }
  };

  const readFavorites = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      const localItems = Array.isArray(value) ? value.filter((item) => item && item.id) : [];
      return uniqueById([...readExternalFavorites(), ...localItems]);
    } catch (_error) {
      return readExternalFavorites();
    }
  };

  const writeFavorites = (items) => {
    window.localStorage.setItem(storageKey, JSON.stringify(uniqueById(items)));
  };

  const getButtonId = (button) => {
    return button.dataset.productId || button.dataset.productUrl || button.dataset.productTitle || '';
  };

  const getButtonItem = (button) => {
    return {
      id: getButtonId(button),
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '',
      price: button.dataset.productPrice || '',
      image: button.dataset.productImage || '',
      category: button.dataset.productCategory || '',
      productType: button.dataset.productType || '',
      variantId: button.dataset.productVariantId || '',
      compareAtPrice: button.dataset.productCompareAt || '',
      badge: button.dataset.productBadge || '',
      addedAt: new Date().toISOString(),
    };
  };

  const setButtonState = (button, isFavorite) => {
    const title = button.dataset.productTitle || '商品';
    button.classList.toggle('is-favorite', isFavorite);
    button.setAttribute('aria-pressed', isFavorite ? 'true' : 'false');
    button.setAttribute('aria-label', isFavorite ? `${title}をお気に入りから削除` : `${title}をお気に入りに追加`);
    button.textContent = '';
  };

  const updateCounters = (favorites) => {
    const count = favorites.length;
    document.querySelectorAll('[data-nini-favorite-count]').forEach((counter) => {
      counter.textContent = String(count);
      counter.hidden = count === 0;
      counter.setAttribute('aria-label', `お気に入り${count}件`);
    });
  };

  const syncButtons = () => {
    const favorites = readFavorites();
    const ids = new Set(favorites.map((item) => String(item.id)));

    document.querySelectorAll('[data-nini-favorite]').forEach((button) => {
      setButtonState(button, ids.has(String(getButtonId(button))));
    });

    updateCounters(favorites);
  };

  const toggleFavorite = (button) => {
    const item = getButtonItem(button);
    if (!item.id) return;

    const favorites = readFavorites();
    const index = favorites.findIndex((favorite) => String(favorite.id) === String(item.id));
    let action = 'add';

    if (index >= 0) {
      favorites.splice(index, 1);
      action = 'remove';
    } else {
      favorites.unshift(item);
    }

    writeFavorites(favorites);
    syncButtons();
    window.dispatchEvent(new CustomEvent('nini:favorites:changed', {
      detail: {
        action,
        item,
        favorites,
        count: favorites.length,
      },
    }));
    track(`nini_favorite_${action}`, {
      product_id: item.id,
      product_title: item.title,
      product_url: item.url,
      favorite_count: favorites.length,
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nini-favorite]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncButtons);
  } else {
    syncButtons();
  }

  window.addEventListener('storage', (event) => {
    if (event.key === storageKey) syncButtons();
  });

  window.addEventListener('nini:wishlist:sync', (event) => {
    const items = Array.isArray(event.detail?.items) ? event.detail.items : [];
    const normalized = uniqueById(items.map(normalizeExternalFavorite).filter(Boolean));
    writeFavorites(normalized);
    syncButtons();
    track('nini_wishlist_sync', { favorite_count: normalized.length });
  });

  window.niniFavoriteButtons = {
    storageKey,
    get: readFavorites,
    sync(items = []) {
      const normalized = uniqueById(items.map(normalizeExternalFavorite).filter(Boolean));
      writeFavorites(normalized);
      syncButtons();
      track('nini_wishlist_sync', { favorite_count: normalized.length });
      return normalized.length;
    },
  };

  let externalPolls = 0;
  const externalPoll = window.setInterval(() => {
    externalPolls += 1;
    syncExternalIfChanged();
    if (externalPolls >= 20) window.clearInterval(externalPoll);
  }, 800);
  window.addEventListener('pageshow', syncExternalIfChanged);
  window.addEventListener('nini:wishlist:ready', syncExternalIfChanged);
})();
